import React, { useState, useRef } from 'react';
import { apiService } from '../services/api';
import { FileUploadProgress, ParseResponse } from '../types';
import './FileUpload.css';

const FileUpload: React.FC = () => {
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress | null>(null);
  const [availableParsers, setAvailableParsers] = useState<string[]>([]);
  const [selectedParser, setSelectedParser] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadHistory, setUploadHistory] = useState<ParseResponse[]>([]);

  React.useEffect(() => {
    // Load available parsers on component mount
    const loadParsers = async () => {
      try {
        const parsers = await apiService.listAvailableParsers();
        setAvailableParsers(parsers);
      } catch (error) {
        console.error('Failed to load parsers:', error);
      }
    };

    loadParsers();
  }, []);

  const handleFileSelect = (file: File) => {
    if (!file) return;

    // Validate file type
    const isCSV = file.type.includes('csv') || file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.type.includes('spreadsheet') || 
                   file.name.toLowerCase().endsWith('.xlsx') || 
                   file.name.toLowerCase().endsWith('.xls');
    const isText = file.type.includes('text') || file.name.toLowerCase().endsWith('.txt');
    
    if (!isCSV && !isExcel && !isText) {
      alert('Please select a CSV, Excel (.xlsx), or Text (.txt) file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 10MB');
      return;
    }

    uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    const uploadState: FileUploadProgress = {
      file,
      progress: 0,
      status: 'uploading',
    };

    setUploadProgress(uploadState);

    try {
      const result = await apiService.uploadFileWithProgress(
        file,
        selectedParser || undefined,
        (progress) => {
          setUploadProgress(prev => prev ? { ...prev, progress } : null);
        }
      );

      setUploadProgress(prev => prev ? {
        ...prev,
        status: 'completed',
        progress: 100,
        result
      } : null);

      // Add to upload history
      setUploadHistory(prev => [result, ...prev]);

      // Reset after 3 seconds
      setTimeout(() => {
        setUploadProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 3000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadProgress(prev => prev ? {
        ...prev,
        status: 'error',
        error: errorMessage
      } : null);

      setTimeout(() => {
        setUploadProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 5000);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading': return '#007bff';
      case 'parsing': return '#ffc107';
      case 'completed': return '#28a745';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'uploading': return 'Uploading...';
      case 'parsing': return 'Parsing...';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      default: return 'Ready';
    }
  };

  return (
    <div className="file-upload">
      {/* Parser Selection */}
      <div className="parser-selection">
        <label htmlFor="parser-select">Bank Parser (optional):</label>
        <select
          id="parser-select"
          value={selectedParser}
          onChange={(e) => setSelectedParser(e.target.value)}
          disabled={uploadProgress?.status === 'uploading'}
        >
          <option value="">Auto-detect</option>
          {availableParsers.map(parser => (
            <option key={parser} value={parser}>
              {parser.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </option>
          ))}
        </select>
      </div>

      {/* Upload Area */}
      <div
        className={`upload-area ${isDragOver ? 'drag-over' : ''} ${uploadProgress ? 'uploading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={!uploadProgress ? handleBrowseClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,.txt"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={uploadProgress?.status === 'uploading'}
        />

        {!uploadProgress ? (
          <div className="upload-prompt">
            <div className="upload-icon">📄</div>
            <p>Drag and drop your CSV, Excel, or Text file here, or click to browse</p>
            <p className="upload-hint">Supports CSV, Excel (.xlsx), and Text (.txt) files up to 10MB</p>
          </div>
        ) : (
          <div className="upload-status">
            <div className="file-info">
              <strong>{uploadProgress.file.name}</strong>
              <span className="file-size">
                ({(uploadProgress.file.size / 1024).toFixed(1)} KB)
              </span>
            </div>

            <div className="progress-container">
              <div
                className="progress-bar"
                style={{
                  width: `${uploadProgress.progress}%`,
                  backgroundColor: getStatusColor(uploadProgress.status)
                }}
              />
            </div>

            <div className="status-info">
              <span
                className="status-text"
                style={{ color: getStatusColor(uploadProgress.status) }}
              >
                {getStatusText(uploadProgress.status)} ({uploadProgress.progress}%)
              </span>
            </div>

            {uploadProgress.error && (
              <div className="error-message">
                {uploadProgress.error}
              </div>
            )}

            {uploadProgress.result && (
              <div className="upload-result">
                <div className="result-summary">
                  ✅ Successfully created {uploadProgress.result.pre_entries_created} pre-entries
                  using {uploadProgress.result.parser_used} parser
                </div>
                {uploadProgress.result.errors && uploadProgress.result.errors.length > 0 && (
                  <div className="result-warnings">
                    <details>
                      <summary>⚠️ {uploadProgress.result.errors.length} warnings</summary>
                      <ul>
                        {uploadProgress.result.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload History */}
      {uploadHistory.length > 0 && (
        <div className="upload-history">
          <h3>Recent Uploads</h3>
          <div className="history-list">
            {uploadHistory.slice(0, 5).map((upload, index) => (
              <div key={index} className="history-item">
                <div className="history-info">
                  <span className="parser-used">{upload.parser_used}</span>
                  <span className="entries-created">
                    {upload.pre_entries_created} entries created
                  </span>
                </div>
                {upload.partial_success && (
                  <span className="partial-success">⚠️ Partial</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;